import { Routes, Route } from 'react-router-dom';
import './App.css';
import Login from './components/Login';
import LoginLayout from './components/layout/LoginLayout';
import { AppRoutes } from './axios/endPoints';
import { AuthPageRouting } from './components/routing/AuthPageRouting';
import { ProtectedRouting } from './components/routing/ProtectedRouting';
import ForgotPassword from './components/ForgotPassword';
import ResetPassword from './components/ResetPassword';
import Register from './components/Register';
import UserManagement from './components/user-managment/user-managment';
import { Roles } from './components/layout/menu-items';
import useNotifications from './components/notifications/notifier';
import { useEffect } from 'react';
import { RootState, useAppDispatch, useAppSelector } from './store/store';
import { getLanguages } from './store/app-slice/app';
import AxiosHandler from './axios/axiosHandler';
import MLPrediction from './components/ml-prediction/MLPrediction';
import ColorPicker, { Colors, defaultColors } from './components/common/ColorPicker';
import MLOptimization from './components/ml-optimization/MLOptimization';
import Help from './components/help/Help';
import OldMLCreator from './components/ml-creator/old_MLCreator';
import MLCreatorSwitcher from './components/ml-creator/MLCreatorSwitcher';
import OldMLTester from './components/ml-tester/old_MLTester';
import MLTesterSwitcher from './components/ml-tester/MLTesterSwitcher';
import { GoogleReCaptchaProvider } from "react-google-recaptcha-v3";
import Profile from './components/profile/profile';
import TierManagement from './components/tier-management/tier-management';
import { fetchUserProfileInfo, clearProfile } from './store/profile-slice/profile-slice';
import { updateCurrentSubscription } from './store/auth-slice/auth-slice';
import { enqueueSnackbar } from './store/app-slice/app';
import { useTranslation } from 'react-i18next';
import { getSubscriptionExpiryNotification, markSubscriptionExpiryNotificationShown } from './services/subscriptionExpiryNotificationService';
import Support from './components/support/support';

function App() {
  const dispatch = useAppDispatch();
  const { languages } = useAppSelector((state: RootState) => state.app);
  const { token } = useAppSelector((state: RootState) => state.auth);
  const { userInfo } = useAppSelector((state: RootState) => state.profile);
  const { t } = useTranslation();

  useNotifications();
  useEffect(() => {
    if (languages.length === 0) {
      dispatch(getLanguages());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const theme: Colors = JSON.parse(localStorage.getItem("appColors") as string) || defaultColors;

    document.body.style.backgroundColor = theme.backgroundColor;
    document.documentElement.style.setProperty('--button-background-color', theme.buttonBackgroundColor);
    document.documentElement.style.setProperty('--button-text-color', theme.buttonTextColor);
    document.documentElement.style.setProperty('--card-header-background-color', theme.cardHeaderBackgroundColor);
    document.documentElement.style.setProperty('--card-header-text-color', theme.cardHeaderTextColor);
    document.documentElement.style.setProperty('--link-text-color', theme.linkTextColor);
    document.documentElement.style.setProperty('--selected-table-row-color', theme.selectedTableRowColor);
    document.documentElement.style.setProperty('--info-icon-color', theme.infoIconColor);
    document.documentElement.style.setProperty('--download-icon-color', theme.downloadIconColor);
    document.documentElement.style.setProperty('--play-icon-color', theme.playIconColor);
    document.documentElement.style.setProperty('--helper-icon-color', theme.helperIconColor);
    document.documentElement.style.setProperty('--delete-icon-color', theme.deleteIconColor);
    document.documentElement.style.setProperty('--redirect-icon-color', theme.redirectIconColor);
    document.documentElement.style.setProperty('--optimization-icon-color', theme.optimizationIconColor);
  }, []);

  useEffect(() => {
    let mounted = true;

    const syncProfileAndSubscription = async () => {
      if (!token) {
        dispatch(clearProfile());
        dispatch(updateCurrentSubscription(null));
        return;
      }

      const action = await dispatch(fetchUserProfileInfo());
      if (!mounted) return;

      if (fetchUserProfileInfo.fulfilled.match(action)) {
        dispatch(updateCurrentSubscription(action.payload?.currentSubscription ?? null));
        return;
      }

      dispatch(updateCurrentSubscription(null));
    };

    void syncProfileAndSubscription();
    return () => {
      mounted = false;
    };
  }, [dispatch, token]);

  useEffect(() => {
    const notification = getSubscriptionExpiryNotification(userInfo?.currentSubscription);
    if (!notification) return;

    const message = notification.daysLeft === 0
      ? t('SUBSCRIPTION_EXPIRES_TODAY')
      : t('SUBSCRIPTION_EXPIRES_IN_DAYS', { days: notification.daysLeft });

    dispatch(
      enqueueSnackbar({
        message,
        options: { variant: 'warning', persist: true },
      })
    );

    markSubscriptionExpiryNotificationShown(
      notification.subscriptionId,
      notification.endDate,
      notification.threshold
    );
  }, [dispatch, t, userInfo?.currentSubscription]);

  return (
    <Routes>
      <Route path={AppRoutes.LOG_IN} element={
        <AuthPageRouting>
            <LoginLayout><Login /></LoginLayout>
        </AuthPageRouting>
      } />
      <Route path={AppRoutes.REGISTER} element={
        <AuthPageRouting>
          <GoogleReCaptchaProvider reCaptchaKey={process.env.REACT_APP_CAPTCHA_SITE_KEY || ''}>
            <LoginLayout><Register /></LoginLayout>
          </GoogleReCaptchaProvider>
        </AuthPageRouting>
      } />
      <Route path={AppRoutes.FORGOT_PASSWORD} element={<LoginLayout><ForgotPassword /></LoginLayout>} />
      <Route path={AppRoutes.RESET_PASSWORD} element={<LoginLayout><ResetPassword /></LoginLayout>} />
      <Route
        path={AppRoutes.ML_CREATOR}
        element={
          <ProtectedRouting roles={[Roles.Admin, Roles.MLAdmin, Roles.MLUser]} checkActiveSubscription={true}>
            <MLCreatorSwitcher />
          </ProtectedRouting>
        }
      />
      <Route
        path={AppRoutes.ML_CREATOR_OLD}
        element={
          <ProtectedRouting roles={[Roles.Admin, Roles.MLAdmin, Roles.MLUser]} checkActiveSubscription={true}>
            <OldMLCreator />
          </ProtectedRouting>
        }
      />
      <Route
        path={AppRoutes.ML_PREDICTION}
        element={
          <ProtectedRouting checkActiveSubscription={true}>
            <MLPrediction showModelSelection={true} />
          </ProtectedRouting>
        }
      />
      <Route
        path={AppRoutes.ML_OPTIMIZATION}
        element={
          <ProtectedRouting checkActiveSubscription={true}>
            <MLOptimization showModelSelection={true} />
          </ProtectedRouting>
        }
      />
      <Route
        path={AppRoutes.ML_TESTER}
        element={
          <ProtectedRouting roles={[Roles.Admin, Roles.MLAdmin, Roles.MLUser]} checkActiveSubscription={true}>
            <MLTesterSwitcher />
          </ProtectedRouting>
        }
      />
      <Route
        path={AppRoutes.ML_TESTER_OLD}
        element={
          <ProtectedRouting roles={[Roles.Admin, Roles.MLAdmin, Roles.MLUser]} checkActiveSubscription={true}>
            <OldMLTester />
          </ProtectedRouting>
        }
      />
      <Route
        path={AppRoutes.COLOR_PICKER}
        element={
          <ProtectedRouting>
            <ColorPicker />
          </ProtectedRouting>
        }
      />
      <Route
        path={AppRoutes.PROFILE}
        element={
          <ProtectedRouting>
            <Profile />
          </ProtectedRouting>
        }
      />
      <Route
        path={AppRoutes.SUPPORT}
        element={
          <ProtectedRouting>
            <GoogleReCaptchaProvider reCaptchaKey={process.env.REACT_APP_CAPTCHA_SITE_KEY || ''}>
            <Support />
            </GoogleReCaptchaProvider>
          </ProtectedRouting>
        }
      />
      <Route
        path={`/help/:sectionId`}
        element={
          <ProtectedRouting>
            <Help />
          </ProtectedRouting>
        }
      />
      <Route
        path={'/help'}
        element={
          <ProtectedRouting>
            <Help />
          </ProtectedRouting>
        }
      />
      <Route
        path={AppRoutes.USER_MANAGEMENT}
        element={
          <ProtectedRouting roles={[Roles.Admin, Roles.MLAdmin]} checkActiveSubscription={true}>
            <UserManagement />
          </ProtectedRouting>
        }
      />
      <Route
        path={AppRoutes.TIER_MANAGEMENT}
        element={
          <ProtectedRouting roles={[Roles.Admin, Roles.MLAdmin]} checkActiveSubscription={true}>
            <TierManagement />
          </ProtectedRouting>
        }
      />
    </Routes>
  );
}

export default AxiosHandler(App);
